import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Navigate } from '@tanstack/react-router';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { WeaveLogo } from '@frontend/assets';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { Page } from '@frontend/page';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  Heading,
  SpinningLoader,
  Stepper,
  Text,
  useFormField,
  styles as dsStyles,
  InlineNotification,
  StepStatus,
} from '@frontend/design-system';
import { BookingSiteInactivePage } from '../../../components/booking-site-inactive-page';
import { useAppointmentRequestForm } from '../../../hooks/booking-site/use-appointment-request-form';
import { useSetupScheduleUiV3FeatureFlag } from '../../../hooks/booking-site/use-setup-schedule-ui-v3-feature-flag';
import { useBookingSiteStore } from '../../../stores/use-booking-site-store';
import { BOOKING_SITE_TRACKING_IDS } from '../../../trackingIds/booking-site-tracking-ids';
import { BOOKING_SITE_HTTP_OPTIONS } from '../../../utils';
import { ApptTypeSelect } from './appt-type-select';
import { Branding } from './branding';
import { useCaptchaV3ShallowStore } from './captcha';
import { InfoForm } from './info-form';
import { LocationSelect } from './location-select';
import { ProviderSelect } from './ProviderSelect';
import { SuccessRequestAppointment } from './success';

dayjs.extend(isoWeek);
dayjs.extend(isBetween);

interface Props {
  locationId: string;
  renderAppointmentTypePaymentsModule: (
    paymentAmount: number,
    locationPortalSlug?: string,
    formError?: string
  ) => JSX.Element;
  isPaymentSuccess?: boolean;
  isPaymentError?: boolean;
  paymentTransactionId?: string;
  resetPaymentDetails?: () => void;
}

export const RequestAppointment = ({
  locationId,
  renderAppointmentTypePaymentsModule,
  isPaymentSuccess = false,
  paymentTransactionId = '',
  resetPaymentDetails,
  isPaymentError = false,
}: Props) => {
  const { t } = useTranslation('bookingSite');

  const {
    data: locationData,
    isError,
    isLoading: isLoadingLocation,
  } = ScheduleQueries.useListLocations(locationId, BOOKING_SITE_HTTP_OPTIONS);
  const isIntegratedOffice = !!locationData?.isIntegrated;
  const officeName = locationData?.officeName ?? '';
  const childLocations = locationData?.childrenLocations ?? [];
  const hasChildren = !!locationData?.childrenLocations?.length;

  const { isLoadingFeatureFlagStatus } = useSetupScheduleUiV3FeatureFlag(
    // NOTE: We need to pass child location to fetch FF value &
    // As V3 FF will be turn on for either all locations or none, so we should be good to check any of the child location's FF status to determine whether to turn on V3 UI or not
    isLoadingLocation ? undefined : childLocations[0]?.locationId ?? locationId,
    isIntegratedOffice
  );

  const truelarkBookingUrl = useMemo(() => {
    const rawUrl = locationData?.truelarkBookingUrl?.trim();
    if (!rawUrl) return '';

    try {
      const parsedUrl = new URL(rawUrl);
      if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') return '';

      return parsedUrl.toString();
    } catch {
      return '';
    }
  }, [locationData?.truelarkBookingUrl]);

  const {
    isSchedularV3Enabled,
    isScheduleDefinitionsEnabled,
    selectedLocationId,
    selectedLocationName,
    paymentLocationPortalSlug,
    selectedAppointmentType,
    selectedAppointmentLeadTime,
    minSlots,
    selectedSlots,
    selectedProvider,
    selectedLocationTimeZone,
    setSelectedLocation,
    clearSelectedAppointmentType,
    clearSelectedProvider,
    setWeekInView,
  } = useBookingSiteStore([
    'isSchedularV3Enabled',
    'isScheduleDefinitionsEnabled',
    'selectedLocationId',
    'selectedLocationName',
    'paymentLocationPortalSlug',
    'setSelectedLocation',
    'selectedAppointmentType',
    'selectedAppointmentLeadTime',
    'selectedProvider',
    'clearSelectedAppointmentType',
    'clearSelectedProvider',
    'setWeekInView',
    'selectedSlots',
    'minSlots',
    'selectedLocationTimeZone',
  ]);
  const { captchaV3Token } = useCaptchaV3ShallowStore('captchaV3Token');
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const [availableOpenings, setAvailableOpenings] = useState(0);

  const timezoneAbbr = useMemo(() => {
    if (!selectedLocationTimeZone || !isSchedularV3Enabled || !selectedProvider) return '';
    const isUserTimezoneDifferent = SchedulerV3.isUserTimezoneOffsetDifferent(
      dayjs.tz.guess(),
      selectedLocationTimeZone
    );

    if (!isUserTimezoneDifferent) return '';

    return SchedulerV3.getTimezoneAbbreviation(selectedLocationTimeZone);
  }, [selectedLocationTimeZone, isSchedularV3Enabled, selectedProvider]);

  const {
    formDetails,
    isFormValid,
    setFormDetails,
    formError,
    isFormCompleted,
    resetFormDetails,
    submitAppointmentRequest,
    isSubmittingRequest,
  } = useAppointmentRequestForm(paymentTransactionId);

  useEffect(() => {
    if (isLoadingLocation) return;

    if (hasChildren) return;

    // set location if its single/child location
    setSelectedLocation(
      locationData?.locationId ?? '',
      locationData?.officeName ?? '',
      locationData?.paymentLocationPortalSlug ?? '',
      locationData?.timezone ?? ''
    );
  }, [locationData, isLoadingLocation, hasChildren, setSelectedLocation]);

  useEffect(() => {
    if (!truelarkBookingUrl) return;

    window.location.replace(truelarkBookingUrl);
  }, [truelarkBookingUrl]);

  const clearSelectedLocation = () => {
    if (hasChildren) {
      setSelectedLocation('', '', '', '');
    }
  };

  const reset = () => {
    clearSelectedAppointmentType();
    clearSelectedProvider();
    clearSelectedLocation();
    resetFormDetails();
    if (paymentLocationPortalSlug) {
      resetPaymentDetails?.();
    }
  };

  const getAppointmentTypeStepValueElement = () => (
    <Text css={styles.cardTitle}>
      {t('Appointment Type')}
      <HeaderDot />
      <Text as='span' weight='light' color='light'>
        {selectedAppointmentType?.name ?? ''}
      </Text>
    </Text>
  );

  const displayProviderSelection = () => {
    let providerName = 'No Preference';
    if (typeof selectedProvider !== 'string') {
      providerName =
        `${selectedProvider?.publicDisplayName}` || `${selectedProvider?.firstName} ${selectedProvider?.lastName}`;
    }
    return (
      <Text as='span' css={styles.cardTitle}>
        {t('Provider & Time')}
        {!!selectedProvider && (
          <>
            <HeaderDot />
            <Text as='span' weight='light' color='light'>
              {providerName}
            </Text>
            <HeaderDot />
            {selectedSlots?.map((date, index, array) => {
              const formattedDate = formatDate(date, 'ddd, MMM D h:mma');
              return (
                <Text key={formattedDate} as='span' weight='light' color='light'>
                  {formattedDate}
                  {index + 1 !== array.length && ', '}
                </Text>
              );
            })}
          </>
        )}
      </Text>
    );
  };

  const complianceCheckBox = useFormField(
    {
      type: 'checkbox',
      required: true,
      value: false,
      touched: false,
    },
    [isFormCompleted]
  );

  // NOTE: First step of three to ensure proper 10DLC compliance in the booking site. This first step is to ensure that the user is aware that they are providing their phone number and that they actively consent to receiving text messages and phone calls from the office they're requesting an appointment from.
  const complianceConsent = () => {
    return (
      <section css={{ marginLeft: theme.spacing(1), display: 'flex', alignItems: 'start' }}>
        <CheckboxField
          {...complianceCheckBox}
          name='10dlc-compliance-consent'
          label='*'
          trackingId={BOOKING_SITE_TRACKING_IDS.patientInfoStep.concentCheckbox}
        />
        {/* // NOTE: To avoid accidental clicks we opted to use a Text component instead of the label property in the CheckboxField component. */}
        <Text css={{ margin: theme.spacing(0, 0, 0, 1) }}>
          {t(
            "I understand that this form is using Google's reCaptcha to verify requests. By providing your phone number, you consent to receiving SMS text messages and phone calls from {{officeName}} – Online Scheduling and Text Connect, including texts and calls that promote our products and services. Text messages and data rates may apply. You may withdraw your consent at any time. Reply STOP to any text message to unsubscribe, or HELP for more options.",
            { officeName: selectedLocationName }
          )}
        </Text>
      </section>
    );
  };

  const isValidSlotSelection =
    (selectedSlots.length > 0 && selectedSlots.length === minSlots) ||
    (!!availableOpenings && selectedSlots.length === availableOpenings);
  const isValidSlotSelectionForV3 =
    selectedSlots.length > 0 && (selectedSlots.length <= minSlots || selectedSlots.length <= availableOpenings);

  const appointmentPaymentAmount = useMemo<number>(() => {
    if (
      !isScheduleDefinitionsEnabled && // Global booking deposit is not applicable for Services currently
      selectedAppointmentType?.hasGlobalBookingDeposit &&
      selectedAppointmentType?.globalBookingDepositAmount
    ) {
      return selectedAppointmentType?.globalBookingDepositAmount;
    } else if (selectedAppointmentType?.hasBookingDeposit && selectedAppointmentType?.bookingDepositAmount) {
      return selectedAppointmentType?.bookingDepositAmount;
    }
    return 0;
  }, [isScheduleDefinitionsEnabled, selectedAppointmentType]);

  // clears the payment details if there is a payment error when user goes back from payment screen
  const handleOnPreviousInPaymentsModule = () => {
    if (!isPaymentSuccess) {
      resetPaymentDetails?.();
    }
  };

  // show payments module if location has portal slug id, selected appointment type has payment amount
  const showPaymentsModule = !!paymentLocationPortalSlug && !!selectedAppointmentType && !!appointmentPaymentAmount;

  const isInfoFormValid = isFormValid && (showPaymentsModule || !!captchaV3Token) && complianceCheckBox.value;

  // condition to enable submit request button
  const isSubmitRequestButtonEnabledInPaymentScreen =
    (isSchedularV3Enabled ? isValidSlotSelectionForV3 : isValidSlotSelection) && isPaymentSuccess && isInfoFormValid;

  // condition to show submit request button in payment screen if form is not valid or payment is not success
  const shouldShowSubmitRequestButtonInPaymentScreen = (!isInfoFormValid && !isPaymentSuccess) || formError;

  // condition to submit booking request on payment success
  const shouldSubmitBookingRequestOnPaymentSuccess =
    paymentTransactionId && showPaymentsModule && isSubmitRequestButtonEnabledInPaymentScreen;

  // useEffect to submit booking request on payment success
  useEffect(() => {
    if (shouldSubmitBookingRequestOnPaymentSuccess) {
      submitAppointmentRequest();
    }
  }, [shouldSubmitBookingRequestOnPaymentSuccess]);

  const stepStatuses = useMemo(() => {
    const firstStepStatus = showPaymentsModule ? 'completed' : 'active';
    const secondStepStatus =
      firstStepStatus === 'completed' && !showPaymentsModule ? 'active' : showPaymentsModule ? 'active' : 'inactive';

    const stepStatus: StepStatus = {
      // Ensure the first step stays completed when the second step is active - this is to maintain the flow of the steps when an appointment type without payments is selected
      1: secondStepStatus === 'active' ? firstStepStatus : 'completed',
      2: firstStepStatus === 'completed' ? secondStepStatus : 'active',
      3: 'inactive',
    };

    const additionalSteps = hasChildren && showPaymentsModule ? [4, 5] : hasChildren || showPaymentsModule ? [4] : [];
    return additionalSteps.reduce((acc, step) => ({ ...acc, [step]: 'inactive' }), { ...stepStatus }) as StepStatus;
  }, [hasChildren, showPaymentsModule]);

  if (isLoadingLocation || isLoadingFeatureFlagStatus) {
    return (
      <div css={[dsStyles.flexCenter, { height: '50vh' }]}>
        <SpinningLoader size='large' css={styles.spinningLoader} />
      </div>
    );
  }

  if (isError) {
    return <Navigate to='/error' replace />;
  }

  if (!locationData?.active && !locationData?.childrenLocations?.length) {
    return <BookingSiteInactivePage locationName={officeName} />;
  }

  if (truelarkBookingUrl) {
    return (
      <div css={[dsStyles.flexCenter, { height: '50vh' }]}>
        <SpinningLoader size='large' css={styles.spinningLoader} />
      </div>
    );
  }

  const patientFullName = getFullName({
    firstName: formDetails?.patientInfo?.firstName,
    lastName: formDetails?.patientInfo?.lastName,
  });

  return (
    <Page css={styles.page}>
      <div css={styles.pageHeader}>
        {officeName ? (
          <Heading level={1} className='request-office-name'>
            {officeName}
          </Heading>
        ) : (
          <span className='request-office-logo'>
            {/* Placeholder until we get the office logo */}
            <WeaveLogo />
          </span>
        )}
        <Heading level={1} className='request-title'>
          {t('Request an Appointment')}
        </Heading>
      </div>
      {isFormCompleted ? (
        <SuccessRequestAppointment reset={reset} />
      ) : (
        <Stepper
          maxWidth={880}
          isHorizontal={isMobile}
          css={styles.stepper(isMobile)}
          stepStatus={hasChildren && paymentLocationPortalSlug ? stepStatuses : undefined}
        >
          {/* select location step */}
          {hasChildren && (
            <Stepper.Card stepValue={selectedLocationName}>
              <Stepper.Title>{t('Select a Location')}</Stepper.Title>
              <LocationSelect locations={childLocations} />
              <Stepper.ButtonBar>
                <Stepper.ClearButton
                  trackingId={BOOKING_SITE_TRACKING_IDS.locationStep.clearButton}
                  clearStep={clearSelectedLocation}
                >
                  {t('Clear')}
                </Stepper.ClearButton>
                <Stepper.NextButton
                  trackingId={BOOKING_SITE_TRACKING_IDS.locationStep.nextButton}
                  isValid={!!selectedLocationId}
                />
              </Stepper.ButtonBar>
            </Stepper.Card>
          )}
          {/* select appointment type step */}
          <Stepper.Card stepValue={getAppointmentTypeStepValueElement()}>
            <Stepper.Title>{t("Let's get you scheduled")}</Stepper.Title>
            <ApptTypeSelect />
            <Stepper.ButtonBar>
              <Stepper.ClearButton
                trackingId={BOOKING_SITE_TRACKING_IDS.appointmentTypeStep.clearButton}
                clearStep={clearSelectedAppointmentType}
              >
                {t('Clear')}
              </Stepper.ClearButton>
              {hasChildren && (
                <Stepper.PreviousButton trackingId={BOOKING_SITE_TRACKING_IDS.appointmentTypeStep.previousButton} />
              )}
              <Stepper.NextButton
                trackingId={BOOKING_SITE_TRACKING_IDS.appointmentTypeStep.nextButton}
                isValid={!!selectedAppointmentType?.id}
              />
            </Stepper.ButtonBar>
          </Stepper.Card>
          {/* select provider & time step */}
          <Stepper.Card stepValue={displayProviderSelection()}>
            <Stepper.Title>{t('Choose your provider & time')}</Stepper.Title>
            <Stepper.Content>
              <ProviderSelect setAvailableOpenings={setAvailableOpenings} />
            </Stepper.Content>
            <Stepper.ButtonBar>
              {selectedProvider && (
                <Stepper.ClearButton
                  trackingId={BOOKING_SITE_TRACKING_IDS.providerStep.clearButton}
                  clearStep={() => {
                    clearSelectedProvider();
                    setWeekInView(selectedAppointmentLeadTime);
                  }}
                >
                  {t('Clear')}
                </Stepper.ClearButton>
              )}
              {!!timezoneAbbr && (
                <InlineNotification
                  size='medium'
                  status='info'
                  message={t('All times are shown in {{timezone}}', { timezone: timezoneAbbr })}
                />
              )}
              <Stepper.PreviousButton trackingId={BOOKING_SITE_TRACKING_IDS.providerStep.previousButton} />
              <Stepper.NextButton
                trackingId={BOOKING_SITE_TRACKING_IDS.providerStep.nextButton}
                isValid={isSchedularV3Enabled ? isValidSlotSelectionForV3 : isValidSlotSelection}
              />
            </Stepper.ButtonBar>
          </Stepper.Card>
          <Stepper.Card>
            <Stepper.Title icon={<Icon name='forms' />}>{t('Patient info')}</Stepper.Title>
            <InfoForm
              formDetails={formDetails || {}}
              setFormDetails={setFormDetails}
              complianceConsent={complianceConsent()}
              isV3CaptchaEnabled={!showPaymentsModule}
              isIntegratedOffice={isIntegratedOffice}
            />
            <Stepper.ButtonBar helperText={formError}>
              <Stepper.PreviousButton trackingId={BOOKING_SITE_TRACKING_IDS.patientInfoStep.previousButton} />
              <Stepper.NextButton
                isValid={isInfoFormValid}
                trackingId={
                  showPaymentsModule
                    ? BOOKING_SITE_TRACKING_IDS.appointmentTypeStep.nextButton
                    : BOOKING_SITE_TRACKING_IDS.submitRequestButton
                }
                onSubmit={!showPaymentsModule ? submitAppointmentRequest : undefined}
              >
                {showPaymentsModule ? t('Next') : t('Submit Request')}
              </Stepper.NextButton>
            </Stepper.ButtonBar>
          </Stepper.Card>
          {showPaymentsModule && (
            <Stepper.Card stepValue={`${patientFullName || t('Payment')} • $${appointmentPaymentAmount}`}>
              <Stepper.Title icon={<Icon name='credit-card' />}>
                {`${patientFullName || t('Payment')} • $${appointmentPaymentAmount}`}
              </Stepper.Title>
              <Stepper.Content>
                {renderAppointmentTypePaymentsModule(
                  appointmentPaymentAmount,
                  paymentLocationPortalSlug,
                  !isFormValid ? formError : ''
                )}
              </Stepper.Content>
              <Stepper.ButtonBar>
                <Stepper.PreviousButton
                  trackingId={BOOKING_SITE_TRACKING_IDS.paymentStep.previousButton}
                  onClick={handleOnPreviousInPaymentsModule}
                />
                {shouldShowSubmitRequestButtonInPaymentScreen && (
                  <Stepper.NextButton
                    trackingId={BOOKING_SITE_TRACKING_IDS.submitRequestButton}
                    isValid={isSubmitRequestButtonEnabledInPaymentScreen}
                    onSubmit={submitAppointmentRequest}
                  >
                    {t('Submit Request')}
                  </Stepper.NextButton>
                )}
                {isPaymentError && (
                  <Stepper.NextButton
                    trackingId={BOOKING_SITE_TRACKING_IDS.paymentStep.retryPaymentButton}
                    isValid
                    onSubmit={resetPaymentDetails}
                  >
                    {t('Retry Payment')}
                  </Stepper.NextButton>
                )}
                {isSubmittingRequest && <SpinningLoader css={{ marginLeft: theme.spacing(2) }} />}
              </Stepper.ButtonBar>
            </Stepper.Card>
          )}
        </Stepper>
      )}
      <Branding />
    </Page>
  );
};

const HeaderDot = () => (
  <Text as='span' css={{ margin: theme.spacing(1) }}>
    &bull;
  </Text>
);

const styles = {
  page: css({
    maxWidth: 900,
    margin: 'auto',
    padding: theme.spacing(2),
    '.request-office-logo, .request-office-name': {
      fontSize: theme.fontSize(24),
      margin: theme.spacing(0),
      '@media (max-width: 699px)': {
        fontSize: theme.fontSize(20),
        marginRight: theme.spacing(1),
      },
    },
    '.request-title': {
      fontSize: theme.fontSize(24),
      color: theme.colors.neutral50,
      margin: theme.spacing(0),
      textAlign: 'right',
      '@media (max-width: 699px)': {
        fontSize: theme.fontSize(20),
      },
    },
    'h2.step-content-title': {
      width: 'auto',
    },
  }),
  stepper: (isMobile: boolean) =>
    css({
      margin: isMobile ? theme.spacing(-1) : theme.spacing(1),
    }),
  pageHeader: css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.colors.neutral20}`,
  }),
  cardTitle: css({
    fontSize: theme.fontSize(20),
    margin: theme.spacing(0),
    width: 'fit-content',
    span: {
      fontWeight: 'normal',
    },
    '@media (max-width: 699px)': {
      fontSize: theme.fontSize(16),
    },
  }),
  cardWidth: css({
    maxWidth: 692,
  }),
  spinningLoader: css({
    margin: 'auto',
  }),
};
